import { Ban, CheckCircle, Clock, Layers, Loader } from "lucide-react";
import { useState } from "react";
import { DataTable, PageHeader, Paginacion, StatsCards } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Modal } from "../../components/ui/primitivos";
import {
  type AlertaFacturacion,
  fetchAlertasFacturacion,
  fetchContadorFacturacion,
  marcarFacturada,
  marcarNoFacturable,
  reabrirFacturacionOt,
  tomarAlerta,
} from "../../lib/consola-api";
import { pesos } from "../../lib/formato";
import { paginar, paginaValida } from "../../lib/paginacion";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { etiquetaDeEstado } from "../produccion/estados";
import { accionesDeLaCola } from "./acciones";
import { CampoArchivoFiscal } from "./CampoArchivoFiscal";
import { DialogoMotivo } from "./DialogoMotivo";

/**
 * Cola de facturación pendiente (ADR 0026).
 *
 * No es una bandeja de avisos: cada fila es un trabajo entregado que todavía no
 * se facturó. Por eso las más viejas van arriba —lo que lleva más tiempo sin
 * facturarse es lo que más urge— y por eso una alerta sigue acá aunque su OT ya
 * esté CERRADA: entregar y facturar son circuitos distintos.
 */

const TONO: Record<string, "ok" | "aviso" | "error" | "neutral"> = {
  pendiente: "error",
  en_proceso: "aviso",
  facturado: "ok",
  no_facturable: "neutral",
  cancelado: "neutral",
};

/**
 * El estado de la alerta se guarda en minúscula y con guion bajo
 * (`en_proceso`); en la interfaz va como se lee.
 */
const ETIQUETA_ALERTA: Record<string, string> = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  facturado: "Facturado",
  no_facturable: "No facturable",
  cancelado: "Cancelado",
};

export function FacturacionPagina() {
  const { can } = useSession();
  const [estado, setEstado] = useState("pendiente");
  const alertas = useDatos(() => fetchAlertasFacturacion(estado || undefined), estado);
  const contador = useDatos(fetchContadorFacturacion);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamanoPagina, setTamanoPagina] = useState(20);
  const [aFacturar, setAFacturar] = useState<AlertaFacturacion | null>(null);
  const [aDecidir, setADecidir] = useState<{
    alerta: AlertaFacturacion;
    accion: "no_facturable" | "reabrir";
  } | null>(null);

  // Reabrir lo hace quien puede pasar una OT a CERRADA, no quien factura (ADR 0099 C4).
  const quienMira = {
    escribir: can("facturacion", "write"),
    decidir: can("produccion", "asignar") || can("produccion", "flexible_transition"),
  };

  /**
   * Veinte por página, el paginador compartido de ADR 0078.
   *
   * Esta pantalla no existe en `imprenta`, así que no hay un tamaño que copiar:
   * se usa el de los demás listados. Sin paginar traía **141 filas de una**, y
   * lo que hay que hacer acá —tomar una alerta y facturarla— se hace de a una.
   *
   * La página se recorta a lo que existe: al cambiar de estado estando en la 4,
   * quedarse ahí muestra una tabla vacía con el paginador diciendo que hay
   * filas.
   */
  const todas = alertas.datos ?? [];
  const paginaActual = paginaValida(pagina, todas.length, tamanoPagina);
  const enPantalla = paginar(todas, paginaActual, tamanoPagina);

  async function accion(alerta: AlertaFacturacion, fn: () => Promise<unknown>) {
    setTrabajando(alerta.id);
    setError(null);
    try {
      await fn();
      await alertas.recargar();
      await contador.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    } finally {
      setTrabajando(null);
    }
  }

  /** Si el worker rechaza, el error sube al diálogo, que queda abierto con lo tipeado. */
  async function facturar(alerta: AlertaFacturacion, facturaRef: string, comprobante: File) {
    await marcarFacturada(alerta.id, { facturaRef, comprobante });
    setAFacturar(null);
    await alertas.recargar();
    await contador.recargar();
  }

  const cuenta = contador.datos ?? {};

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Facturación pendiente"
        subtitulo="Trabajos entregados que todavía no se facturaron"
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {alertas.error ? <Aviso>{alertas.error}</Aviso> : null}

      {/* Los estados como tarjetas y no como botones sueltos: el conteo por
          estado ES la estadística, y clickearla filtra. Es el patrón del
          tablero de `imprenta`. */}
      <StatsCards
        estadisticas={[
          {
            titulo: "Pendientes",
            valor: cuenta.pendiente ?? 0,
            icono: Clock,
            activa: estado === "pendiente",
            alClickear: () => setEstado("pendiente"),
          },
          {
            titulo: "En proceso",
            valor: cuenta.en_proceso ?? 0,
            icono: Loader,
            activa: estado === "en_proceso",
            alClickear: () => setEstado("en_proceso"),
          },
          {
            titulo: "Facturadas",
            valor: cuenta.facturado ?? 0,
            icono: CheckCircle,
            activa: estado === "facturado",
            alClickear: () => setEstado("facturado"),
          },
          {
            titulo: "No se facturan",
            valor: cuenta.no_facturable ?? 0,
            icono: Ban,
            activa: estado === "no_facturable",
            alClickear: () => setEstado("no_facturable"),
          },
          {
            titulo: "Todas",
            valor: cuenta.total ?? 0,
            icono: Layers,
            activa: estado === "",
            alClickear: () => setEstado(""),
          },
        ]}
      />

      <DataTable
        datos={enPantalla}
        clave={(a) => a.id}
        cargando={alertas.cargando}
        mensajeVacio={estado === "pendiente" ? "Nada pendiente de facturar" : "Sin resultados"}
        columnas={[
          {
            clave: "ot",
            encabezado: "OT",
            render: (a) => (
              <>
                <div className="font-mono text-sm">{a.numero}</div>
                {/* Que la OT esté CERRADA y la alerta pendiente es legítimo,
                    pero conviene verlo: es el caso que se pierde de vista. */}
                <div className="text-neutral-500 text-xs">{etiquetaDeEstado(a.estadoOrden)}</div>
              </>
            ),
          },
          { clave: "cliente", encabezado: "Cliente", render: (a) => a.cliente },
          {
            clave: "producto",
            encabezado: "Producto",
            render: (a) => (
              <span className="text-neutral-600">
                {a.producto} <span className="text-neutral-400">×{a.cantidad}</span>
              </span>
            ),
          },
          { clave: "total", encabezado: "Total", render: (a) => pesos(a.total) },
          {
            clave: "estado",
            encabezado: "Estado",
            render: (a) => (
              <>
                <Etiqueta tono={TONO[a.estado] ?? "neutral"}>
                  {ETIQUETA_ALERTA[a.estado] ?? a.estado}
                </Etiqueta>
                {a.procesadoPor ? (
                  <div className="text-neutral-500 text-xs">{a.procesadoPor}</div>
                ) : null}
                {a.motivoNoFacturable && a.estado === "no_facturable" ? (
                  <div className="max-w-xs text-neutral-500 text-xs">{a.motivoNoFacturable}</div>
                ) : null}
              </>
            ),
          },
          {
            clave: "factura",
            encabezado: "Factura",
            render: (a) => <span className="font-mono text-sm">{a.facturaRef ?? "—"}</span>,
          },
          {
            clave: "acciones",
            encabezado: "",
            className: "text-right",
            render: (a) => {
              const acciones = accionesDeLaCola(a.estado, quienMira);
              return (
                <>
                  {acciones.includes("tomar") ? (
                    <Boton
                      variante="secundario"
                      disabled={trabajando === a.id}
                      onClick={() => accion(a, () => tomarAlerta(a.id))}
                    >
                      Tomar
                    </Boton>
                  ) : null}
                  {acciones.includes("no_facturable") ? (
                    <Boton
                      variante="secundario"
                      disabled={trabajando === a.id}
                      onClick={() => setADecidir({ alerta: a, accion: "no_facturable" })}
                    >
                      No se factura
                    </Boton>
                  ) : null}
                  {acciones.includes("facturar") ? (
                    <Boton
                      variante="primario"
                      disabled={trabajando === a.id}
                      onClick={() => setAFacturar(a)}
                    >
                      Facturar
                    </Boton>
                  ) : null}
                  {acciones.includes("reabrir") ? (
                    <Boton
                      variante="secundario"
                      disabled={trabajando === a.id}
                      onClick={() => setADecidir({ alerta: a, accion: "reabrir" })}
                    >
                      Reabrir
                    </Boton>
                  ) : null}
                </>
              );
            },
          },
        ]}
      />

      <DialogoFacturar
        alerta={aFacturar}
        onCerrar={() => setAFacturar(null)}
        onConfirmar={facturar}
      />

      <DialogoMotivo
        abierto={aDecidir !== null}
        titulo={
          aDecidir?.accion === "reabrir"
            ? `Reabrir ${aDecidir.alerta.numero}`
            : `No se factura ${aDecidir?.alerta.numero ?? ""}`
        }
        descripcion={
          aDecidir?.accion === "reabrir"
            ? "La decisión de no facturar se deshace y la OT vuelve a pedir que alguien decida."
            : "La alerta deja de pedir factura. El motivo queda registrado."
        }
        confirmar={aDecidir?.accion === "reabrir" ? "Reabrir" : "No se factura"}
        onCerrar={() => setADecidir(null)}
        onConfirmar={async (motivo) => {
          if (!aDecidir) return;
          const { alerta, accion: cual } = aDecidir;
          if (cual === "reabrir") await reabrirFacturacionOt(alerta.produccionId, motivo);
          else await marcarNoFacturable(alerta.id, motivo);
          await alertas.recargar();
          await contador.recargar();
        }}
      />

      {todas.length > 0 ? (
        <Paginacion
          pagina={paginaActual}
          tamano={tamanoPagina}
          total={todas.length}
          alCambiarPagina={setPagina}
          alCambiarTamano={(t) => {
            setTamanoPagina(t);
            setPagina(1);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Número de factura y comprobante, los dos obligatorios (ADR 0099 A6, C2). El
 * worker revalida todo; acá se evita mandar un pedido incompleto o un archivo
 * que no sirve. Un rechazo del worker se muestra adentro y el diálogo no se
 * cierra: lo tipeado y el archivo elegido siguen ahí para corregir y reintentar.
 */
function DialogoFacturar({
  alerta,
  onCerrar,
  onConfirmar,
}: {
  alerta: AlertaFacturacion | null;
  onCerrar: () => void;
  onConfirmar: (alerta: AlertaFacturacion, facturaRef: string, comprobante: File) => Promise<void>;
}) {
  const [facturaRef, setFacturaRef] = useState("");
  const [comprobante, setComprobante] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const listo = facturaRef.trim().length > 0 && comprobante !== null;

  function limpiar() {
    setFacturaRef("");
    setComprobante(null);
    setError(null);
  }

  function cerrar() {
    limpiar();
    onCerrar();
  }

  return (
    <Modal
      abierto={alerta !== null}
      onCerrar={cerrar}
      titulo={alerta ? `Facturar ${alerta.numero}` : "Facturar"}
      descripcion="Cargá el número de la factura y su comprobante."
    >
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!alerta || !comprobante || !listo) return;
          setEnviando(true);
          setError(null);
          try {
            await onConfirmar(alerta, facturaRef.trim(), comprobante);
            limpiar();
          } catch (err) {
            setError(err instanceof Error ? err.message : "No se pudo facturar");
          } finally {
            setEnviando(false);
          }
        }}
      >
        <Campo
          etiqueta="Número de factura"
          name="facturaRef"
          value={facturaRef}
          onChange={(e) => setFacturaRef(e.target.value)}
          required
        />
        <CampoArchivoFiscal
          etiqueta="Comprobante"
          name="comprobante"
          alElegir={(archivo) => {
            setComprobante(archivo);
            if (archivo) setError(null);
          }}
          alRechazar={setError}
        />

        {error ? <Aviso>{error}</Aviso> : null}

        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={cerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={!listo || enviando}>
            {enviando ? "Facturando…" : "Facturar"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}
